import { type IntlShape } from 'react-intl-next';

import { getAnonymousAvatarUrl, getAnonymousName } from '@canvas/anonymous-assets';
import { getCollaboratorTokenColor } from '@canvas/constants/get-color-value';
import { type Collaborator, type Collaborators } from '@canvas/frame-api-types';
import { type Anonymous, type Identity, IdentityKind } from '@canvas/model/identity';
import { type ClientId } from '@canvas/model/telepointers';

import { COLORS_TO_OMIT } from './constants';

export enum IdentityOwner {
  you = 'yourName',
  someoneElse = 'collaboratorName',
}

const getNicknameFromIdentity = (
  identity: Identity,
  clientId: ClientId,
  kind: IdentityOwner,
  formatMessage: IntlShape['formatMessage']
): string =>
  identity.type === IdentityKind.AnonymousId
    ? formatMessage(getAnonymousName(clientId)[kind])
    : identity.displayName ?? '';

const identityToPartialCollaborator = (
  identity: Identity,
  clientId: number,
  ownerType: IdentityOwner,
  formatMessage: IntlShape['formatMessage']
) => {
  const color = getCollaboratorTokenColor(clientId, COLORS_TO_OMIT);
  const name = getNicknameFromIdentity(identity, clientId, ownerType, formatMessage);

  return {
    userId: identity.accountId,
    clientId,
    color,
    name,
  } satisfies Partial<Collaborator>;
};

export const authenticatedIdentityToCollaborator = (
  identity: Exclude<Identity, Anonymous>,
  clientId: number,
  ownerType: IdentityOwner,
  formatMessage: IntlShape['formatMessage']
) => {
  return {
    ...identityToPartialCollaborator(identity, clientId, ownerType, formatMessage),
    avatar: { src: identity.picture },
  } satisfies Collaborator;
};

export const anonymousIdentityToCollaborator = async (
  identity: Anonymous,
  clientId: number,
  ownerType: IdentityOwner,
  formatMessage: IntlShape['formatMessage']
): Promise<Collaborator> => {
  const svg = await getAnonymousAvatarUrl(clientId);

  return {
    ...identityToPartialCollaborator(identity, clientId, ownerType, formatMessage),
    avatar: {
      src: svg,
    },
  };
};

export const userIdentityToCollaborator = async (
  identity: Identity,
  clientId: number,
  ownerType: IdentityOwner,
  formatMessage: IntlShape['formatMessage']
) =>
  identity.type === IdentityKind.AnonymousId
    ? await anonymousIdentityToCollaborator(identity, clientId, ownerType, formatMessage)
    : authenticatedIdentityToCollaborator(identity, clientId, ownerType, formatMessage);

export const participantsToCollaborators = async (
  currentUser: Identity,
  currentUserClientId: ClientId,
  participants: Record<number, Identity>,
  formatMessage: IntlShape['formatMessage']
): Promise<Collaborators> => {
  const me = await userIdentityToCollaborator(
    currentUser,
    currentUserClientId,
    IdentityOwner.you,
    formatMessage
  );

  const collaborators: Collaborator[] = await Promise.all(
    Object.entries(participants).map(([clientId, participant]) => {
      return userIdentityToCollaborator(
        participant,
        Number.parseInt(clientId),
        IdentityOwner.someoneElse,
        formatMessage
      );
    })
  );

  return { collaborators, me };
};
